// ChallengePricingPage — The "Choose your challenge" page mirroring the marketing 3-tier table
// EXACT values from the marketing site: $50K/$79, $100K/$149, $150K/$219
// All conditions: profit target, daily loss, max DD, contracts, consistency, profit share, etc.
// Subscription model: monthly recurring, free activation, 30-day crypto access

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TierPill } from '../../components/Brand';
import { TfSectionHeaderView, TfCard, TfCardFloating, TfStatRow } from '../../components/TfPrimitives';

type Tier = 'Beginner' | 'Mid' | 'Professional';
type AccountSize = '50K' | '100K' | '150K';

interface TierData {
  key: Tier;
  account: AccountSize;
  monthly: number;
  crypto: number;  // 30-day crypto access price
  promo: number;   // gold offer
  contracts: string;
  profitTarget: { amount: number; pct: number };
  dailyLossFunded: { amount: number; pct: number };
  maxDD: { amount: number; pct: number };
  consistencyEval: number;   // 40% eval / 50% funded
  consistencyFunded: number;
  profitShare: number;        // 80% (90% after 3rd reward)
  emoji: string;
  color: string;
  popular?: boolean;
}

const TIERS: TierData[] = [
  {
    key: 'Beginner', account: '50K', monthly: 79, crypto: 69, promo: 69,
    contracts: '5 Standard / 50 Micros',
    profitTarget: { amount: 3000, pct: 6 },
    dailyLossFunded: { amount: 1000, pct: 2 },
    maxDD: { amount: 2000, pct: 4 },
    consistencyEval: 40, consistencyFunded: 50, profitShare: 80,
    emoji: '🌱', color: '#34C759',
  },
  {
    key: 'Mid', account: '100K', monthly: 149, crypto: 129, promo: 129,
    contracts: '10 Standard / 100 Micros',
    profitTarget: { amount: 6000, pct: 6 },
    dailyLossFunded: { amount: 2000, pct: 2 },
    maxDD: { amount: 3000, pct: 3 },
    consistencyEval: 40, consistencyFunded: 50, profitShare: 80,
    emoji: '🚀', color: '#007AFF', popular: true,
  },
  {
    key: 'Professional', account: '150K', monthly: 219, crypto: 189, promo: 189,
    contracts: '15 Standard / 150 Micros',
    profitTarget: { amount: 9000, pct: 6 },
    dailyLossFunded: { amount: 3000, pct: 2 },
    maxDD: { amount: 4500, pct: 3 },
    consistencyEval: 40, consistencyFunded: 50, profitShare: 80,
    emoji: '💎', color: '#1C1C1E',
  },
];

const CONDITION_LABELS = [
  { key: 'monthly',   label: 'Monthly Fee',      sub: 'Recurring · cancel anytime', tooltip: 'Billed on the same day each calendar month. Cancel anytime from your Dashboard.' },
  { key: 'activation',label: 'Activation Fee',   sub: 'Free during evaluation', tooltip: 'No activation fee. After passing, no further fee — funded accounts are no-cost.' },
  { key: 'contracts', label: 'Contracts',        sub: 'Mix Standard + Micros', tooltip: 'You may use a mix of Standard/Mini contracts with Micro contracts (1 Standard = 10 Micros).' },
  { key: 'target',    label: 'Profit Target',    sub: 'Reach to pass evaluation', tooltip: 'Your account balance must reach the target to complete the Challenge.' },
  { key: 'dailyLoss', label: 'Daily Pause (Funded)', sub: 'Auto-flatten, no account fail', tooltip: 'If you lose this amount in a day, your positions close and account freezes until 16:05 CT.' },
  { key: 'maxDD',     label: 'Max Drawdown',     sub: 'Trails until reaches initial', tooltip: 'Trails based on EOD balance until it reaches your starting balance, then fixed at that level.' },
  { key: 'duration',  label: 'Challenge Duration', sub: 'Unlimited days', tooltip: 'No time limit. Reset anytime to keep remaining days (e.g., 3 days used + 27 remaining).' },
  { key: 'profit',    label: 'Profit Share',     sub: '80% from day 1 · 90% after 3rd reward', tooltip: 'You keep 80% of all profits from your first payout. After 3 payouts, increase to 90%.' },
  { key: 'consistency',label: 'Consistency Rule', sub: '40% Eval · 50% Funded', tooltip: 'If daily profit exceeds 40% (eval) / 50% (funded) of target, excess is added to your target at 16:00 CT.' },
  { key: 'reset',     label: 'Reset',            sub: 'Anytime, keep remaining days · $69/129/189', tooltip: 'You can reset your account anytime, as often as you like, keeping the remaining challenge days.' },
];

export function ChallengePricingPage() {
  const nav = useNavigate();
  const [selected, setSelected] = useState<Tier>('Mid');
  const [billing, setBilling] = useState<'monthly' | 'crypto'>('monthly');
  const t = TIERS.find((x) => x.key === selected)!;
  const price = billing === 'monthly' ? t.monthly : t.crypto;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border ios26-navbar px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue haptic">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold">Choose your challenge</div>
        <div className="w-9" />
      </div>

      {/* Hero — white floating card with massive price + pill buttons */}
      <div className="mx-4 mt-4">
        <TfCardFloating className="text-center">
          <div className="text-[13px]" style={{ color: '#8E8E93' }}>Account balance</div>
          <div className="mt-1 flex items-baseline justify-center gap-1">
            <span className="text-[40px] font-bold tabular-nums leading-none" style={{ color: '#1C1C1E' }}>${price}</span>
          </div>
          <div className="mt-1 flex items-center justify-center gap-1 text-[13px]">
            <span className="font-semibold text-tf-profit">+6.0%</span>
            <span className="text-tf-text-3">(+${(price * 0.06).toFixed(0)})</span>
            <span className="text-tf-text-3">· Today</span>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <button className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform" data-testid="buy-monthly">
              Buy
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 17L17 7" /><path d="M7 7h10v10" />
              </svg>
            </button>
            <button onClick={() => nav('/portal/payouts')} className="flex h-12 items-center justify-center gap-1.5 rounded-full bg-tf-text-1 text-[15px] font-semibold text-white active:scale-[0.97] transition-transform">
              Sell
              <svg viewBox="0 0 24 24" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 7L7 17" /><path d="M17 17H7V7" />
              </svg>
            </button>
          </div>
        </TfCardFloating>
      </div>

      {/* Tier selector */}
      <div className="mx-4 mt-4 flex justify-center">
        <div className="inline-flex rounded-full border border-tf-border bg-white p-1">
          {TIERS.map((x) => (
            <button
              key={x.key}
              onClick={() => setSelected(x.key)}
              data-testid={`tier-pick-${x.key}`}
              className={['rounded-full px-5 py-2 text-[13px] font-bold transition',
                selected === x.key ? (x.popular ? 'bg-tf-blue text-white' : 'bg-tf-text-1 text-white') : 'text-tf-text-3'
              ].join(' ')}
            >
              {x.key}
              {x.popular && selected !== x.key && <span className="ml-1 text-[9px] text-tf-warning">★</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Selected tier summary card */}
      <div className="mx-4 mt-3">
        <TfCard className="p-5" hoverable>
          <div className="flex items-center gap-3 mb-3">
            <div className="flex h-12 w-12 items-center justify-center rounded-[14px] text-[28px]" style={{ backgroundColor: t.color + '20' }}>{t.emoji}</div>
            <div className="flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-[18px] font-bold text-tf-text-1">{t.key}</span>
                <TierPill tier={t.key} />
                {t.popular && <span className="rounded-full bg-tf-orange px-1.5 py-0.5 text-[9px] font-bold uppercase text-white">Most popular</span>}
              </div>
              <div className="text-[12px] text-tf-text-3">${t.account} account · {t.contracts}</div>
            </div>
            <div className="text-right">
              <div className="text-[24px] font-bold tabular-nums text-tf-text-1">${price}</div>
              <div className="text-[10px] text-tf-text-3">/{billing === 'monthly' ? 'month' : '30 days'}</div>
            </div>
          </div>
          <div className="ios26-seg" data-testid="billing-toggle">
            <button onClick={() => setBilling('monthly')} className={billing === 'monthly' ? 'active' : ''}>Monthly · ${t.monthly}</button>
            <button onClick={() => setBilling('crypto')} className={billing === 'crypto' ? 'active' : ''}>Crypto · ${t.crypto}</button>
          </div>
        </TfCard>
      </div>

      {/* 3-up stat row for key metrics */}
      <TfStatRow
        className="mt-4"
        stats={[
          { label: 'Profit target', value: `${t.profitTarget.pct}%`, color: '#34C759' },
          { label: 'Max drawdown', value: `${t.maxDD.pct}%`, color: '#FF3B30' },
          { label: 'Profit share', value: `${t.profitShare}%`, color: '#007AFF' },
        ]}
      />

      {/* Full conditions table (per marketing site) */}
      <TfSectionHeaderView title="Conditions" viewAllLabel="View all" onViewAll={() => {}} />
      <div className="mx-4">
        <TfCard>
          {CONDITION_LABELS.map((c) => {
            let val = '—';
            if (c.key === 'monthly')    val = `$${t.monthly}/mo`;
            if (c.key === 'activation') val = 'Free';
            if (c.key === 'contracts')  val = t.contracts;
            if (c.key === 'target')     val = `$${t.profitTarget.amount.toLocaleString()} (${t.profitTarget.pct}%)`;
            if (c.key === 'dailyLoss')  val = `$${t.dailyLossFunded.amount.toLocaleString()} (${t.dailyLossFunded.pct}%)`;
            if (c.key === 'maxDD')      val = `$${t.maxDD.amount.toLocaleString()} (${t.maxDD.pct}%)`;
            if (c.key === 'duration')   val = 'Unlimited';
            if (c.key === 'profit')     val = `${t.profitShare}% / 90% after 3rd`;
            if (c.key === 'consistency')val = `${t.consistencyEval}% Eval / ${t.consistencyFunded}% Funded`;
            if (c.key === 'reset')      val = `$${t.promo}`;
            return (
              <div key={c.key} data-testid={`cond-${c.key}`} className="px-4 py-3 border-b border-tf-border last:border-b-0 flex items-center justify-between">
                <div>
                  <div className="text-[14px] font-semibold text-tf-text-1">{c.label}</div>
                  <div className="text-[11px] text-tf-text-3">{c.sub}</div>
                </div>
                <div className="text-right text-[14px] font-bold text-tf-blue tabular-nums">{val}</div>
              </div>
            );
          })}
        </TfCard>
      </div>

      {/* Key benefits */}
      <TfSectionHeaderView title="Key features" />
      <div className="mx-4 grid grid-cols-2 gap-2">
        {[
          { emoji: '⏱️', label: 'Unlock rewards in 5 profitable days' },
          { emoji: '📉', label: 'Max DD trails until initial' },
          { emoji: '🛡️', label: 'Daily Pause — no failed accounts' },
          { emoji: '📈', label: 'Scaling buying power' },
          { emoji: '🔁', label: 'Copy trading across accounts' },
          { emoji: '🆔', label: 'Quick KYC & setup' },
          { emoji: '💰', label: 'Up to 90% profit split' },
          { emoji: '📋', label: 'Up to 5 funded accounts' },
        ].map((b) => (
          <div key={b.label} className="rounded-[16px] bg-white p-2.5 flex items-center gap-2" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.04)' }}>
            <span className="text-[18px]">{b.emoji}</span>
            <span className="text-[12px] font-medium text-tf-text-1 leading-tight">{b.label}</span>
          </div>
        ))}
      </div>

      {/* CTA */}
      <div className="mx-4 mt-4">
        <button onClick={() => nav('/portal/challenge/' + t.key.toLowerCase())} data-testid="start-challenge" className="flex h-12 w-full items-center justify-center btn-spring">
          Start trading {t.key} · ${t.account}K
        </button>
        <p className="mt-2 text-center text-[10px] text-tf-text-3">7-day money-back · 24/7 support · Cancel anytime</p>
      </div>
    </div>
  );
}
